// App-side delegate for `@heidi/templates/data`.
// isAuthenticated and the flag cross the package seam as booleans, and the
// error toast stayed; the sort-order branch and the module-scope invalidator
// moved.
//
// The flag is a VALUE here, unlike flag getters that resolve through
// `posthog.isFeatureEnabled` and emit an exposure on read:
// `useIsPinnedTemplateNotegenUiEnabled` is an OpenFeature `useBooleanFlagValue`
// hook that already resolves every render. There is no exposure to defer, so
// a getter would add indirection and buy nothing.
import { useIsPinnedTemplateNotegenUiEnabled } from '@/features/flags';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { toast } from '@heidi/design-system/toast';
import { GET_PINNED_TEMPLATES } from '@heidi/templates/contracts';
import {
  invalidatePinnedTemplates,
  useQueryPinnedTemplates as useQueryPinnedTemplatesCore,
} from '@heidi/templates/data';
import type { AxiosError } from 'axios';
import { useCallback } from 'react';

export { GET_PINNED_TEMPLATES, invalidatePinnedTemplates };

export function useQueryPinnedTemplates(options?: { enabled?: boolean }) {
  const { isAuthenticated } = useIsAuthenticated();
  const isPinnedNotegenUiEnabled = useIsPinnedTemplateNotegenUiEnabled();

  const onRequestError = useCallback((error: AxiosError) => {
    toast({
      tone: 'destructive',
      title: 'Error',
      description: error.message,
    });
  }, []);

  return useQueryPinnedTemplatesCore(
    options,
    isAuthenticated,
    isPinnedNotegenUiEnabled,
    onRequestError
  );
}
